/**
 * UI view models — the entire contract between the game and the UI layer.
 *
 * Nothing in src/ui imports from sim/, combat/, meta/ or entity/. The UI depends
 * on nothing; the integrator writes adapters that project real game state into
 * these plain objects. The dependency arrow points one way only, which is what
 * lets the UI be built, typechecked and demoed against mock data while the
 * combat and meta workstreams are still in flight.
 *
 * Ids and vocabularies (weapon class, module family, damage type, primer kind,
 * tag) are plain strings rather than enums for the same reason: the content
 * workstreams own those vocabularies and the UI must not gate their growth.
 * The only closed unions here are the ones the *stylesheet* has to know about —
 * rarity, slot kind, hand.
 */

// ------------------------------------------------------------------ atoms

/**
 * `gameover` is in this union rather than in `ui/scene.ts`'s `AppScene` on
 * purpose, and the choice is the whole design of docs/23: a scene transition
 * tears the `Game` down, and the world has to keep running behind the death
 * screen. So death is a *panel*, in the same modal layer as the pause menu,
 * over a sim that is still stepping.
 */
export type Screen = 'none' | 'loadout' | 'codex' | 'map' | 'pause' | 'gameover';
export type Hand = 'main' | 'off';
export type Archetype = 'melee' | 'projectile';
export type ToastKind = 'discovery' | 'unlock' | 'warn';

/** Closed because the palette has one colour per rarity. */
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'relic' | 'cursed';

/** Closed because docs/04 §5.1 fixes the five slot types. */
export type SlotKind = 'core' | 'kinetic' | 'payload' | 'trigger' | 'frame';

export const SLOT_ORDER: readonly SlotKind[] = ['core', 'kinetic', 'payload', 'trigger', 'frame'];
export const RARITY_ORDER: readonly Rarity[] = [
  'common',
  'uncommon',
  'rare',
  'epic',
  'relic',
  'cursed',
];

/**
 * A captured cell region, one palette index per cell.
 *
 * This is the form the codex actually stores (docs/03 §7). A dataURL would be
 * ~1.4 KB per 16×16 thumbnail and a PNG encode per capture; an indexed bitmap
 * is one byte per cell and needs no encoder at either end, which is what lets
 * the store go into the save file without contradicting docs/01's "the save is
 * a seed plus a diff log precisely to stay small".
 *
 * `palette` is indexed by the byte in `data` and holds packed 0xRRGGBB.
 * A negative entry is transparent — index 0 is VOID, which is the absence of
 * matter and must not paint as black.
 */
export interface IndexedThumbVM {
  w: number;
  h: number;
  palette: readonly number[];
  data: Uint8Array;
}

/**
 * A captured codex thumbnail. The sim captures the moment; the UI only
 * displays it. Three forms are accepted so the capture path can hand over an
 * indexed bitmap with no encode cost at all, an ImageBitmap, or a dataURL if it
 * came from somewhere that only had one.
 */
export type ThumbVM = string | ImageBitmap | IndexedThumbVM;

// ------------------------------------------------------------------ stats

export type StatFormat = 'int' | 'dec1' | 'dec2' | 'pct' | 'ms' | 'x';

export interface StatVM {
  key: string;
  label: string;
  value: number;
  format?: StatFormat;
  unit?: string;
  /** Default true. Weight, windup and recovery set this false. */
  higherIsBetter?: boolean;
}

/** A module's contribution to one stat, for the install preview. */
export interface StatModVM {
  key: string;
  label: string;
  delta: number;
  /** 'add' → +4 dmg; 'mul' → ×1.2 cadence. */
  mode: 'add' | 'mul';
  format?: StatFormat;
  higherIsBetter?: boolean;
}

// ------------------------------------------------------------------ combat HUD

export interface HandVM {
  hand: Hand;
  /**
   * What this hand's key cap says — #134.
   *
   * The card used to print `LMB` / `RMB`, passed into `Hud`'s constructor once
   * and never touched again, from a default argument no caller ever overrode.
   * That was authored rather than resolved twice over: it was not the key a
   * keyboard player presses, and no rebinding could ever move it. It arrives
   * through the view model now, from the same binding table the ability strip
   * and the settings screen read, so the panel spells no key of its own.
   */
  keyHint: string;
  /** Definition id — what the glyph, name and rarity are looked up by. */
  weaponId: string | null;
  /**
   * Which *roll* is in this hand. Two instances of one definition are two
   * different weapons with their own modules, ammo and heat, and the HUD has to
   * be able to tell them apart (docs/04 §7).
   */
  instanceId: string | null;
  name: string;
  /** One char. Class identity at a glance; the HUD never guesses it. */
  glyph: string;
  rarity: Rarity;
  /** Index into the current combo string, 0-based; -1 when idle. */
  comboStep: number;
  comboLength: number;
  comboNodeName: string;
  /** 0 = just used, 1 = ready. Drives the recovery bar. */
  ready01: number;
  ready: boolean;
  /** Charge weapons (bow draw, rifle, rail). null when the weapon has none. */
  charge01: number | null;
  /** Heat (drill, beam) — the self-risk read. null when not applicable. */
  heat01: number | null;
  /** Locked out cooling down. Heat at 1.0 and heat *refusing to fire* differ. */
  overheated: boolean;
  ammo: number | null;
  ammoMax: number | null;
  reloading: boolean;
  /** 0 at the start of the reload, 1 when the magazine is back. */
  reload01: number;
  /**
   * The weapon's own reservoir — fuel, hunger, spin-up, battery — as distinct
   * from `charge01`, which is a held attack. The four kinds are four different
   * weapon identities, so the gauge is labelled: a spin-up you must keep alive
   * and a fuel tank you must refill are opposite instructions. null when the
   * weapon has no reservoir worth a gauge.
   */
  reserve01: number | null;
  /**
   * `guard` is the shield's, and it is the one that reads *down* from full:
   * blocking spends it and a shield with none left breaks. Named in the union
   * because a gauge the HUD cannot label is a gauge the player cannot price,
   * and "a bar went down when I blocked" is not a mechanic anyone can learn.
   */
  reserveKind: '' | 'fuel' | 'hunger' | 'spin' | 'battery' | 'guard';
  /** Set while this hand's next hit would detonate a primer already on target. */
  detonationArmed: boolean;
}

/**
 * docs/04 §8 — alternating hands fills the meter. `nextHand` is the whole point
 * of surfacing it: the player needs to know which hand keeps the chain alive.
 */
export interface RhythmVM {
  value01: number;
  tier: number;
  tierMax: number;
  nextHand: Hand | null;
  decaying: boolean;
  /** Plain-language payoff, e.g. "+18% dmg · −12% recovery". */
  bonusText: string;
}

/**
 * docs/01 §6.4 — statuses are countable glyphs, never colour washes. The glyph
 * comes from the VM so three simultaneous statuses are distinguishable by
 * *shape*; colour is a secondary cue only.
 */
export interface StatusVM {
  id: string;
  name: string;
  glyph: string;
  stacks: number;
  /** 1 = just applied, 0 = about to expire. Negative duration → permanent. */
  remaining01: number;
  kind: 'buff' | 'debuff' | 'neutral';
  desc?: string;
}

/**
 * docs/02 §6.5 — one button drives all nine traversal abilities and the context
 * picks which. That scheme only works if the player can see which one is armed
 * *before* pressing, so `armed` is the load-bearing field here: without it the
 * HUD can report what was used last, which is the one thing nobody needs to
 * know. `charges` and `cooldown01` are the other half of the read — an ability
 * that is armed but spent has to look different from one that is ready.
 */
export interface AbilityVM {
  id: string;
  name: string;
  glyph: string;
  keyHint: string;
  unlocked: boolean;
  ready: boolean;
  /** 1 = off cooldown. Always 1 for abilities that have no cooldown. */
  cooldown01: number;
  /** True when pressing the ability button right now would fire *this* one. */
  armed: boolean;
  /** Spendable charges; null when the ability has no charge economy at all. */
  charges: number | null;
  chargesMax: number | null;
}

/**
 * The gathering field (#122, surfaced by #129).
 *
 * #122 shipped the field with its whole tell as log lines, and deferred the
 * HUD. Two of the three facts it produces were then unreadable: the tier, and —
 * the one that matters — that the field is dragging something that will hurt
 * you. The field pulls lava. Its safety argument is that you get 650 ms of
 * warning (measured: warned at 15 cells, arrives 39 ticks later at 60 Hz), and
 * a warning that only appears in the run log is a warning nobody sees.
 *
 * Both readings are shaped as *countable glyphs* rather than washes or bars,
 * per docs/01 §6.4 — the same argument that decided the status chips, and the
 * same one that collapsed the ability row beside this block.
 */
export interface GatherVM {
  /** Is a hold open? Everything below is only meaningful while it is. */
  active: boolean;
  /** Index into the hold ladder, 0-based; -1 while closed. */
  tier: number;
  /** How many tiers there are, so the pip row is countable against a total. */
  tierMax: number;
  /**
   * The nearest hazardous thing the field can actually move, named. Empty when
   * there is none — which is not the same as a field that is closed, and the
   * panel must not render the two the same way.
   */
  hazard: string;
  /**
   * How far in the hazard has come, 0 at the rim of the field and 1 on top of
   * you. A fraction of the radius rather than a raw distance because the radius
   * is what the player chose by holding: "half way in" is the actionable read,
   * "nine cells" is not.
   */
  hazardClose01: number;
  /** How many hazardous cells are in the field. One lava cell is not a flood. */
  hazardCount: number;
}

export interface PlayerVM {
  hp: number;
  hpMax: number;
  shield: number;
  shieldMax: number;
  dashCharges: number;
  dashMax: number;
  /** Fill of the charge currently regenerating. */
  dashRecharge01: number;
  /**
   * Issue #171. Whether this archetype carries a flight/hover-thrust fuel
   * meter at all (`verticalMode` is `'flight'` or `'hover-thrust'`) — a
   * `'double-jump'` body has nothing here to show, not an empty meter.
   */
  hasFlightMeter: boolean;
  /** Fraction of flight/hover-thrust fuel remaining. 0 when `hasFlightMeter` is false. */
  flightFuel01: number;
  main: HandVM;
  off: HandVM;
  rhythm: RhythmVM;
  gather: GatherVM;
  statuses: StatusVM[];
  abilities: AbilityVM[];
  /**
   * The one button seven of the nine traversal abilities share (docs/02 §6.5),
   * as it should be printed on a cap.
   *
   * The strip states it once rather than on every glyph, so it is a property of
   * the row and not of any ability — and it is here rather than as a constant in
   * `ui/hud.ts` because resolving it means reading the binding table, which a
   * panel is not allowed to import. Panels take `viewModels` and `style`, and
   * that is the whole reason this field exists.
   */
  abilityKey: string;
  /**
   * The run purse. Scrip is earned per kill and **lost on death**; Ash is
   * banked and compounds forever (docs/05 §4). They are shown together and
   * styled apart on purpose — a run-scoped currency that looks like a permanent
   * one gets hoarded, which is precisely the behaviour it is designed against.
   */
  scrip: number;
  ash: number;
}

// ------------------------------------------------------------------ inventory

export interface SlotVM {
  kind: SlotKind;
  /** Nth slot of this kind on the weapon, 0-based. */
  index: number;
  moduleId: string | null;
}

export interface CodexRefVM {
  /** 'material' | 'reaction' | 'enemy' — routed by the codex panel. */
  kind: 'material' | 'reaction' | 'enemy';
  id: string;
  label: string;
}

export interface WeaponVM {
  /**
   * Definition id. This is a *catalogue* identity: two rolls of the same
   * weapon share it, which is correct for a reference table and wrong for an
   * inventory — see `instanceId`.
   */
  id: string;
  /**
   * Which roll this row is, or null for a catalogue row that is not a real
   * holding and cannot be equipped. Opaque; never parse it. Everything the
   * inventory keys by — slots, hands, loadouts, pairs, and every callback —
   * uses this, because keying by definition collapses two rolls of one weapon
   * into a single row whose MAIN/OFF buttons both point at the same object.
   */
  instanceId: string | null;
  name: string;
  /** Display name of the class, e.g. "Greatblade". */
  className: string;
  archetype: Archetype;
  rarity: Rarity;
  glyph: string;
  damageType: string;
  /** Thematic module family this weapon leans into, if any. */
  family: string | null;
  tags: string[];

  /** Ordered for the comparison table; the first 4–6 are the ones that matter. */
  stats: StatVM[];

  /** Primer kinds applied / consumed. Fold installed modules in before handing over. */
  primes: string[];
  detonates: string[];
  /** Verb used in synergy prose: "shatters", "ignites", "harvests". */
  detonateVerb?: string;
  /** Verb phrase for what it lays down: "sprays ICE", "coats in OIL". */
  primeVerb?: string;

  /** docs/04 §6, plain language: "destroys hardness ≤ 4 in a 6-unit arc". */
  terrainText: string;
  /** Prose, also the free-text search corpus. */
  effectText: string;
  /** docs/04 §5.3 — self-risk is first-class and must be visible, not buried. */
  selfRiskText?: string;

  slots: SlotVM[];
  favourite: boolean;
  isNew?: boolean;
  codexRefs?: CodexRefVM[];
}

export interface ModuleVM {
  id: string;
  name: string;
  family: string;
  slot: SlotKind;
  rarity: Rarity;
  glyph: string;
  tags: string[];
  effectText: string;
  /** What the detonation actually pays out — used verbatim in synergy prose. */
  detonationPayoff?: string;
  damageType?: string;
  primes: string[];
  detonates: string[];
  statMods: StatModVM[];
  selfRiskText?: string;
  /**
   * Weapon class names this fits, or null for universal. Drives the
   * "slot compatibility" filter.
   */
  compatibleClasses: string[] | null;
  /**
   * The `WeaponVM` *key* it currently sits in (`keyOf`), or null when loose.
   * An instance id, because "installed" is a property of one roll and not of
   * every copy of that definition. Never consumed.
   */
  installedOn: string | null;
  favourite: boolean;
  isNew?: boolean;
  codexRefs?: CodexRefVM[];
}

export interface LoadoutSlotVM {
  index: number;
  name: string;
  filled: boolean;
  /** One-line "Greatblade + Cryo Sprayer · 7 modules". */
  summary: string;
  weaponIds: string[];
  /**
   * The installed module set per hand, positionally parallel to `weaponIds`
   * (#17). docs/04 §7 specifies a loadout as "weapons + modules" and only the
   * weapon ids were stored, so saving, respeccing and restoring gave you a
   * different build under the same name: the gear came back, the decisions did
   * not. Instance ids in `weaponIds` are what make this unambiguous — a
   * snapshot names *which roll* it re-dresses, not merely which weapon.
   */
  moduleIds: string[][];
  /** Modules in the snapshot, summed across hands. The summary quotes it. */
  moduleCount: number;
  /**
   * Every weapon in the snapshot is still held. Instances leave the run —
   * stashed, dropped, or belonging to a run that ended — and a LOAD that
   * silently restores half a build is worse than one that refuses out loud.
   */
  available: boolean;
  /** Why it cannot be restored, in the player's words. Empty when available. */
  unavailableText: string;
  savedAt?: number;
}

/** docs/04 §7 — two saved pairs on a single hotkey. */
export interface QuickPairVM {
  index: number;
  name: string;
  mainId: string | null;
  offId: string | null;
  active: boolean;
}

/**
 * One thing in the profile stash (#5).
 *
 * A *definition*, not a roll. The stash outlives the run and instance ids do
 * not, so what survives a death is "a Cryo Sprayer with these two modules in
 * it", never "that particular Cryo Sprayer". That is a real cost and the panel
 * states it rather than implying otherwise — hence `moduleNames`, which is what
 * comes back with the item, spelled out on the row.
 */
export interface StashItemVM {
  /** Stable across save/load. Every stash action names this, never `defId`. */
  id: string;
  /** Definition id, for the catalogue lookup behind the glyph and rarity. */
  defId: string;
  kind: 'weapon' | 'module';
  name: string;
  rarity: Rarity;
  glyph: string;
  /** Weapons only: the modules screwed into it, restored with it. */
  moduleNames: string[];
}

/**
 * The profile stash — docs/04 §7's "8 weapon slots + unlimited stash at any
 * rest node", bounded in practice by the three `ash.stash.*` unlocks (#5).
 *
 * Kept apart from `InventoryVM.slots` rather than being "everything past slot
 * 8", because the two differ in the thing that matters: carried weapons die
 * with the run, stashed ones are the whole point of the unlock. A tail of the
 * carried array cannot say that, which is why six rows of the loadout panel sat
 * permanently null under a header advertising a store that did not exist.
 */
export interface StashVM {
  items: StashItemVM[];
  /** Slots earned so far. 0 until the first `ash.stash.*` unlock is bought. */
  capacity: number;
  used: number;
  free: number;
  /** Contents may be moved right now: at a rest node, with slots to use. */
  open: boolean;
  /**
   * Why it is shut, in the player's words. "Locked" on its own is
   * indistinguishable from broken, and the two reasons have opposite remedies —
   * one is a place to walk to, one is a purchase. Empty when `open`.
   */
  hint: string;
}

/**
 * Crafting — the rest-node workbench (#8).
 *
 * Inputs come from a *carried pouch*, not from a set of materials you have seen,
 * and that is the whole reason the panel shows `have` beside `need` on every
 * line: against a seen-set a recipe is permanently available or permanently
 * not, crafting the same thing twice costs exactly what crafting it once did,
 * and there is nothing to run out of. A quantity is a cost; a checklist is not.
 */
export interface CraftInputVM {
  /** Codex id of the material. `label` is what the row prints. */
  material: string;
  label: string;
  need: number;
  have: number;
}

export interface PouchEntryVM {
  material: string;
  label: string;
  units: number;
}

export interface RecipeVM {
  id: string;
  name: string;
  desc: string;
  tier: string;
  /** "make" · "convert" · "move" — the three tiers read as three verbs. */
  verb: string;
  /** The full input list, not only the gaps: the shortfall is a fact about a cost. */
  inputs: CraftInputVM[];
  craftable: boolean;
  /**
   * Arguments the panel has to collect before the button does anything —
   * `fromMaterial`, `toMaterial`, `weapon`, `twoWeapons`, `module`.
   */
  needs: readonly string[];
  /** Non-empty when the recipe is shown but cannot be run. */
  hint: string;
}

export interface CraftingVM {
  open: boolean;
  /** Why the workbench is unusable. Empty when open. */
  hint: string;
  /**
   * A recipe naming a material this profile has never recorded is absent
   * entirely rather than listed as unaffordable — the player cannot act on a
   * name they have never seen. Unknown and unaffordable are different sentences
   * and only one of them belongs in this list.
   */
  recipes: RecipeVM[];
  pouch: PouchEntryVM[];
  pouchUsed: number;
  pouchCap: number;
}

/** What the panel collects for a recipe's `needs`, reported with the click. */
export interface CraftArgsVM {
  fromMaterial?: string;
  toMaterial?: string;
  weaponInstanceId?: string;
  toWeaponInstanceId?: string;
  moduleId?: string;
}

export interface InventoryVM {
  /** Bump on any mutation. Panels re-render only when this changes. */
  revision: number;
  /**
   * docs/04 §7 — 8 weapon slots you carry. null = empty. Holds **instance**
   * ids (`keyOf`), not definition ids. The stash is `stash`, not the tail of
   * this: see `StashVM` for why the two cannot be one array.
   */
  slots: (string | null)[];
  /** Instance ids, for the same reason `slots` is. */
  hands: { main: string | null; off: string | null };
  /** Every weapon the profile holds — carried *and* stashed. */
  weapons: WeaponVM[];
  modules: ModuleVM[];
  loadouts: LoadoutSlotVM[];
  pairs: QuickPairVM[];
  stash: StashVM;
  /** The workbench. Lives here because the pouch is genuinely something you carry. */
  crafting: CraftingVM;
  /** Respec is free everywhere, but stash access is rest-node gated. */
  atRestNode: boolean;
  /**
   * True when `weapons`/`modules` are the whole game catalogue rather than the
   * run's holdings. A debug affordance only — the panel says so out loud,
   * because a browsable catalogue of gear you cannot obtain is indistinguishable
   * from a broken inventory unless it is labelled.
   */
  catalogue: boolean;
}

// ------------------------------------------------------------------ synergy

export interface SynergyLineVM {
  subject: string;
  /** "(off-hand)", "(carried)" — where it is right now. */
  where: string;
  /** The stated reason, in plain language. Never a bare number. */
  reason: string;
  refId: string;
}

/** docs/04 §7.1 — the exact shape the spec renders. */
export interface SynergyHintVM {
  weaponId: string;
  title: string;
  primes: string[];
  detonates: string[];
  modulesDetonatingMyPrimers: SynergyLineVM[];
  weaponsPrimingMyDetonations: SynergyLineVM[];
  terrainText: string;
  warnings: string[];
}

// ------------------------------------------------------------------ codex

export interface MaterialVM {
  id: string;
  name: string;
  category: string;
  tags: string[];
  /** CSS colour for the swatch. The codex shows matter as matter. */
  color: string;
  discovered: boolean;
  /** Property readout; only the ones worth reading, adapter decides. */
  props: StatVM[];
  /** Ids into `CodexVM.reactions`. */
  knownReactionIds: string[];
  note?: string;
}

export interface ReactionEntryVM {
  id: string;
  /** The pair, as display names. */
  a: string;
  b: string;
  condition: string;
  outcome: string;
  /** The rule's `event` string from docs/03 §7, if it had one. */
  event?: string;
  /** Monotonic discovery counter — the codex is ordered by this, not by id. */
  discoveredAt: number;
  seenCount: number;
  thumb?: ThumbVM;
  /**
   * Fallback for `thumb`: the colours of the two reactants and the product.
   * Nothing captures reaction frames yet, and a permanently blank 64px box next
   * to every row reads as a broken image rather than as a pending feature — a
   * three-band swatch is at least the true colour of the thing that happened.
   * Sides resolved through a tag rather than a concrete material have no colour.
   */
  swatch?: { a: string | null; b: string | null; out: string | null };
  discovered: boolean;
  /**
   * What the Reaction Hints passive is allowed to say about a reaction you have
   * not seen — "Missing reagent: Oil." Empty when the passive is unheld or when
   * there is nothing honest to say, which is the common case: a rule with
   * neither reagent recorded gives no hint at all, because naming both would
   * hand over the discovery rather than nudge toward it.
   */
  hint: string;
}

export interface EnemyEntryVM {
  id: string;
  name: string;
  discovered: boolean;
  killCount: number;
  deathsTo: number;
  composition: Array<{ mat: string; fraction: number; color: string }>;
  /** Derived from composition, never authored — docs/03 §6. State the reason. */
  weaknesses: Array<{ label: string; reason: string }>;
  resistances: Array<{ label: string; reason: string }>;
  behaviour: string;
  /**
   * The #132 cue family of the body's core material — docs/15 §2.1.2. One word
   * and a look, so the player can decide before the fight what the weakness
   * rows only confirm during it.
   */
  cue: { name: string; tell: string; keys: string };
  /**
   * The three arrays above are withheld pending the Composition Autopsy passive
   * (`codex.autopsy`). Composition is the *cause* and weaknesses are its
   * consequence, so they are hidden together — showing one would leak the whole
   * system while pretending not to.
   *
   * A flag rather than an absence, because a blank section reads as missing
   * data and a locked one reads as something to go and earn. That difference is
   * the entire value of putting the passive behind a track.
   */
  redacted: boolean;
  /** What to print in place of the withheld block. Empty when nothing is. */
  redactedHint: string;
}

export interface CodexVM {
  revision: number;
  materials: MaterialVM[];
  reactions: ReactionEntryVM[];
  enemies: EnemyEntryVM[];
  /** For the completion readouts at the top of each section. */
  totals: { materials: number; reactions: number; enemies: number };
}

// ------------------------------------------------------------------ progression

/** docs/05 §4 — the five tracks. Closed union: the panel labels them. */
export type TrackId = 'ash' | 'codex' | 'lineage' | 'salvage' | 'attunement';

export interface TrackUnlockVM {
  name: string;
  at: number;
  unlocked: boolean;
  desc: string;
}

export interface TrackVM {
  id: TrackId;
  name: string;
  /** "deaths", "discovering reactions" — the behaviour that feeds it. */
  fedBy: string;
  glyph: string;
  value: number;
  /** Threshold of the next unlock; equals `value` when the track is maxed. */
  next: number;
  level: number;
  unlocks: TrackUnlockVM[];
}

export interface ProgressionVM {
  revision: number;
  tracks: TrackVM[];
}

// ------------------------------------------------------------------ map

/**
 * The room kinds the map is allowed to draw (#25).
 *
 * Deliberately closed, and deliberately shorter than it was. `boss` and `shop`
 * were styled for a long time and never generated: the map is the thing the
 * player *navigates by*, so a room kind the world does not place is worse than
 * no map at all. Adding a member here is a promise that `WorldGen.roomKindAt`
 * returns it — `asMapRoomKind` enforces the promise in the other direction.
 */
export type MapRoomKind = 'sanctum' | 'rest' | 'surface' | 'normal';

export const MAP_ROOM_KINDS: readonly MapRoomKind[] = ['sanctum', 'rest', 'surface', 'normal'];

/**
 * Legend text, one line per drawn kind. A three-colour map with no key is a
 * puzzle; `normal` is deliberately empty because the unmarked room is the
 * default and naming it would fill the legend with noise.
 */
export const MAP_KIND_LABEL: Readonly<Record<MapRoomKind, string>> = {
  sanctum: 'sanctum — the seal holds; your checkpoint',
  rest: 'rest node — bench, stash, and whoever is trading',
  surface: 'surface — where the run began',
  normal: '',
};

/**
 * Narrow whatever the world said into something the map can honestly draw.
 *
 * The integrator hands over `WorldGen.roomKindAt`'s raw string. Anything this
 * file has no rule for collapses to `normal` rather than reaching the DOM as an
 * unstyled `data-kind`, which would render as an ordinary room *and* imply the
 * UI knows something it does not.
 */
export function asMapRoomKind(s: string): MapRoomKind {
  return s === 'sanctum' || s === 'rest' || s === 'surface' ? s : 'normal';
}

export interface MapRoomVM {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  visited: boolean;
  current: boolean;
  /**
   * What the room *is*, styled by data attribute. Never 'current' — where you
   * are standing is `current`'s job, and spending this field on it is what made
   * the sanctum under your feet stop looking like a sanctum.
   */
  kind: MapRoomKind;
  biome: string;
}

export interface MapVM {
  revision: number;
  rooms: MapRoomVM[];
  biomeName: string;
  mode: string;
  /**
   * The current biome's ambient hazard, in the biome's own words
   * (`BiomeHazard.desc`). Null when the integrator has none to give. The
   * arrival toast is where the player first reads it; this is where they go
   * back to re-read it, which a toast cannot serve.
   */
  hazardText: string | null;
}

// ------------------------------------------------------------------ factions

/** Mirrors `meta/factions.ts` `Standing`. Ordered high to low. */
export type FactionStanding =
  | 'exalted' | 'allied' | 'friendly' | 'neutral' | 'wary' | 'hostile' | 'nemesis';

export const FACTION_STANDINGS: readonly FactionStanding[] = [
  'exalted', 'allied', 'friendly', 'neutral', 'wary', 'hostile', 'nemesis',
];

export interface FactionVM {
  id: string;
  name: string;
  /** −100..100. The sign is the whole story; the magnitude is the detail. */
  reputation: number;
  standing: FactionStanding;
  /** They will trade with you at all. */
  shopOpen: boolean;
  /** 0..3 — how deep their stock goes. 0 is a closed door. */
  offerTier: number;
  hostile: boolean;
  /** 0..1 — the share of their presence in the world that spawns hostile. */
  hostileRate: number;
  /** What they want done, in their own words. The "how do I fix this" line. */
  wants: string;
  /** What they sell, so a standing has a visible thing to be worth. */
  gives: readonly string[];
}

/**
 * One opposed pair, as one bar (#11).
 *
 * The pair shares a single signed axis in `meta/factions.ts`, so rep(a) is
 * always exactly −rep(b). Two independent bars would draw that as a coincidence;
 * one bar with a needle draws it as the mechanism, which is the point — the
 * player has to *see* that pleasing one is displeasing the other, not read it in
 * a patch note.
 */
export interface FactionAxisVM {
  id: string;
  /** The positive pole and its opposite. `marker01` is measured toward `pos`. */
  pos: FactionVM;
  neg: FactionVM;
  /** 0 = fully `neg`, 0.5 = untouched, 1 = fully `pos`. */
  marker01: number;
  /** True while the needle has never left centre — a fact, not an error. */
  untouched: boolean;
}

/** A deed the world observed, and who it flattered. */
export interface FactionDeedVM {
  id: string;
  name: string;
  factionName: string;
  count: number;
  /** Reputation moved per occurrence. `count * weight` is the total swing. */
  weight: number;
}

export interface FactionsVM {
  revision: number;
  axes: FactionAxisVM[];
  /** Biggest swing first — the answer to "why do they hate me". */
  deeds: FactionDeedVM[];
  /** Nothing has moved yet. The panel says so instead of drawing six zeroes. */
  untouched: boolean;
}

/** Where the needle sits on a −100..100 axis, clamped. */
export function repMarker01(rep: number, max = 100): number {
  const t = (rep + max) / (max * 2);
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/**
 * The colour a standing earns. Standing is a word and a word alone is slow to
 * read at a glance, so it also carries a tone — but the word is always present,
 * because colour on its own is not a readout (docs/01 §6.4).
 */
export function standingTone(s: FactionStanding): 'good' | 'dim' | 'warn' | 'bad' {
  if (s === 'exalted' || s === 'allied') return 'good';
  if (s === 'friendly' || s === 'neutral') return 'dim';
  if (s === 'wary') return 'warn';
  return 'bad';
}

/**
 * The sentence a reputation change is worth showing as it happens.
 *
 * Both halves or neither: a toast that says only "Emberwrights +10" hides the
 * half of the system that makes it a decision. #11's acceptance criterion is
 * that opposed-axis movement is *observable*, and this is where it is observed —
 * the codex tab is where it is studied afterwards.
 */
export function factionSwingText(
  gainedName: string,
  lostName: string,
  delta: number,
): string {
  const d = Math.abs(Math.round(delta));
  if (d === 0) return '';
  const up = delta > 0 ? gainedName : lostName;
  const down = delta > 0 ? lostName : gainedName;
  return `${up} +${d} · ${down} −${d}`;
}

// ------------------------------------------------------------------ vendor

export interface VendorOfferVM {
  id: string;
  label: string;
  /** Scrip — the run currency (#29). Never Ash; Ash is between runs (#10). */
  price: number;
  affordable: boolean;
  /** Why this line cannot be taken. Empty when it can — every refusal names
   *  its own cause, the same contract crafting keeps. */
  refusal: string;
}

/**
 * An in-world vendor, in the altar's shape (#11).
 *
 * Not a shop screen. docs/13 §5 records the altar — one thing, in the world,
 * claimed by standing at it and pressing E — as the most Noita-unlike and
 * best-received interaction in the build, so this reuses it rather than adding
 * a second, weaker interaction. Out of range it shows a name and a standing and
 * nothing else: reading the stock should require walking over.
 */
export interface VendorVM {
  factionId: string;
  factionName: string;
  standing: FactionStanding;
  inRange: boolean;
  /** They are trading. False shows `refusal` where the stock would be. */
  open: boolean;
  /**
   * They would rather kill you. The stall is still *there* — #11 asks that
   * hostile standing have a consequence past a closed door, so this is drawn as
   * a warning rather than as an absence.
   */
  hostile: boolean;
  refusal: string;
  /** The whole shelf, including lines standing has not opened yet. */
  offers: VendorOfferVM[];
  /**
   * The line currently on the counter — the one `E` takes. The player does not
   * choose it; the clock does, exactly as at the altar (docs/13 §5). Null when
   * there is nothing on the counter at all.
   */
  counterId: string | null;
  /** Seconds until the counter turns over. The altar's clock, kept. */
  rotateIn: number;
  /** What the player has to spend, so the price has something to be judged against. */
  scrip: number;
}

// ------------------------------------------------------------------ root

/**
 * Action id -> the key cap printed for it, on the table the game is actually
 * being played on — issues #134 and #135.
 *
 * Built once per binding change by `UIAdapter.setBindings`, from
 * `bindingRows(table, {})`: the same function the settings screen, the title
 * legend and the in-run hint line print from. An action with no key resolves to
 * `'unbound'`, which is a statement the surfaces are expected to print rather
 * than a hole to paper over with the key it used to be on.
 *
 * A map rather than a per-surface field because the surfaces that need it are
 * not all inside the HUD: the prologue overlay teaches five of these actions
 * and reaches them through `UIRoot.update`, which is handed this whole object.
 */
export type KeyLabelsVM = ReadonlyMap<string, string>;

export interface GameVM {
  player: PlayerVM;
  inventory: InventoryVM;
  codex: CodexVM;
  progression: ProgressionVM;
  map: MapVM | null;
  /**
   * Standing with all six factions (#11). Always present — an all-zero ledger
   * is the honest state of a fresh profile, and the panel that says "nothing has
   * moved yet" teaches the system exists before any deed has fired.
   */
  factions: FactionsVM;
  /** The vendor the player is standing at, if any. Null is the common case. */
  vendor: VendorVM | null;
  /**
   * Every key cap the interface can print, resolved (#135).
   *
   * The HUD's four caps are resolved into `player` before they get here, because
   * they are fields of a card that is being filled anyway. This is for the
   * surfaces that are not: the prologue overlay, whose prompts name an action
   * and print whatever it is on.
   */
  keyLabels: KeyLabelsVM;
  /** Purely informational. Panels never pause anything themselves. */
  paused: boolean;
}

/**
 * Everything the UI can ask the game to do. All optional — an integrator can
 * mount the UI read-only and wire actions up incrementally. Panels report
 * intent and nothing else; pausing, validation and persistence are the
 * integrator's business.
 */
export interface UICallbacks {
  onEquip?(weaponId: string, hand: Hand): void;
  onUnequip?(hand: Hand): void;
  /** Deposit a carried weapon into the profile stash. Keyed by instance. */
  onStash?(weaponId: string): void;
  /**
   * Take something back out, keyed by `StashItemVM.id`. Without it the stash is
   * a one-way drop box, which is not a store — the whole point is that a weapon
   * you put away two runs ago can come back (#5).
   */
  onUnstash?(stashItemId: string): void;
  /**
   * Run a recipe (#8). The panel reports the arguments it collected; spending
   * the pouch and producing the result are the integrator's business, and the
   * refusal message comes back the same way — every refusal names its own cause.
   */
  onCraft?(recipeId: string, args: CraftArgsVM): void;
  onInstallModule?(weaponId: string, slot: SlotKind, slotIndex: number, moduleId: string): void;
  onRemoveModule?(weaponId: string, slot: SlotKind, slotIndex: number): void;
  onToggleFavourite?(kind: 'weapon' | 'module', id: string): void;
  onSaveLoadout?(index: number, name: string): void;
  onRestoreLoadout?(index: number): void;
  onClearLoadout?(index: number): void;
  onSelectPair?(index: number): void;
  /**
   * Snapshot the equipped pair into quick-pair `index`. Without this the two
   * pairs can never hold anything, so the radial's hub and the pair rows are
   * permanently "— + —" (docs/04 §7, "two saved pairs on a single hotkey").
   */
  onSavePair?(index: number): void;
  /** Reports the screen the UI moved to. The integrator decides about pause. */
  onScreenChange?(screen: Screen): void;
  onCodexOpenRef?(ref: CodexRefVM): void;
  onResume?(): void;
  onQuit?(): void;
  /**
   * The death screen's "Continue" — issue #23.
   *
   * Deliberately **not** `onQuit`, though both start a fresh run and the
   * temptation to reuse it is strong. `onQuit` means "end this run *and*
   * restart"; by the time this fires the run has already been ended and banked
   * — that is what the numbers on the screen are — so routing Continue through
   * `onQuit` would bank it a second time: Ash paid twice, `stats.deaths`
   * incremented twice, the Courier's carry collected twice. Two verbs, two
   * callbacks, and the double-bank is unrepresentable rather than something a
   * reader has to notice.
   */
  onContinueAfterRun?(): void;
  /**
   * The application shell's two exits from the pause menu.
   *
   * They are not the run's business — settings and the title screen both
   * outlive the Game — but the pause menu is the only place the player can
   * reach them from mid-run, so they arrive here like everything else rather
   * than through a module-level singleton the UI has to remember to consult.
   */
  onOpenSettings?(): void;
  onQuitToTitle?(): void;
  /**
   * Which of those two exits actually exist right now.
   *
   * A **predicate**, not a value, and the difference is load-bearing: the
   * integrator registers the exits after the UI is constructed, so a boolean
   * pair captured at construction reads false forever and the menu silently
   * drops both rows. The pause menu asks this at the moment it builds its rows,
   * which is the first time the player opens it.
   */
  hasShellExits?(): { settings: boolean; quitToTitle: boolean };
}

// -------------------------------------------------------- keyboard ownership

/** Where the focus is, relative to the run UI. */
export interface FocusPlacement {
  /** Is anything focused at all? `document.body` counts as nothing. */
  focused: boolean;
  /** Inside one of the run UI's own layers (hud / modal / overlay). */
  inOwnLayers: boolean;
  /** Inside the host element the run UI shares with the application shell. */
  inSharedHost: boolean;
}

/**
 * May the run UI swallow this keystroke?
 *
 * `UIRoot` binds Escape, Tab, C and M on *window* in the capture phase and
 * calls `stopPropagation` while any of its panels is open. That is right while
 * the player is in the loadout — a panel that lets WASD leak into the player
 * controller gets you killed — and wrong the moment the thing on screen belongs
 * to the application shell instead. The settings screen mounts into the same
 * host and focuses itself, and a capture-phase listener registered earlier will
 * happily eat every key before it arrives: no cursor, no key rebinding, and an
 * Escape that closes the pause menu *behind* the screen rather than the screen.
 *
 * So ownership follows the focus. Nothing focused, or focus in our own layers,
 * or focus outside the shared host entirely (the canvas) — ours. Focus
 * somewhere else inside the shared host — someone else's screen is up, and it
 * is theirs.
 */
export function uiOwnsKeyboard(where: FocusPlacement): boolean {
  if (!where.focused) return true;
  if (where.inOwnLayers) return true;
  return !where.inSharedHost;
}

/** What `uiOwnsEscape` needs to know. */
export interface EscapeClaim {
  /** The run UI's current panel, or `'none'`. */
  screen: string;
  /** `prologueVM.skippable` — is the hold-to-skip prompt on screen right now? */
  prologueSkippable: boolean;
}

/**
 * May the run UI swallow **Escape** in particular? — issue #82.
 *
 * `uiOwnsKeyboard` answers "is this window's keyboard ours", which is about
 * focus. This answers a second question that only Escape raises, because Escape
 * is the one key two systems both have a claim on.
 *
 * The prologue skips on a *held* `pause`, and `Escape -> 'pause'` is in
 * `DEFAULT_BINDINGS`. But `UIRoot` binds on `window` in the **capture** phase
 * and `Input` listens in the bubble phase, so for as long as `UIRoot` answered
 * Escape by opening the pause menu and calling `stopPropagation()`, `Input`
 * never saw the key at all: `held('pause')` could only ever become true from a
 * gamepad, and the hold, `PROLOGUE_SKIP_HOLD_MS` and the prompt drawn beside it
 * were all authored against a control a keyboard player cannot operate.
 *
 * The rule is deliberately narrower than "the UI ignores Escape during the
 * prologue". A first run cannot skip — the hold is gated on `prologueSeen` —
 * so yielding the key for the whole scene would leave Escape doing *nothing*
 * on precisely the playthrough where a new player is most likely to press it.
 * Instead the key follows the offer, and the offer is already a fact the UI
 * holds: `prologueVM.skippable` is what decides whether the prompt is drawn.
 * One fact, one owner, and Escape is never dead — whoever does not own it
 * opens the pause menu.
 *
 * A panel outranks the hold. Escape closes what is open, or a loadout opened
 * during a skippable prologue would be a trap.
 *
 * `Game.pollMenuButtons` gates the pad's Start button on the same predicate
 * (`prologueSkipOffered`, #78), so the two input paths agree by construction
 * rather than by comment — `tests/prologue.test.ts` asserts they do.
 */
export function uiOwnsEscape(claim: EscapeClaim): boolean {
  if (claim.screen !== 'none') return true;
  return !claim.prologueSkippable;
}

// ------------------------------------------------------------- pause menu

/**
 * The pause menu's entries.
 *
 * `abandon` and `quitToTitle` are genuinely different verbs and the menu must
 * not blur them: abandoning banks the run and drops you into a *fresh* run on a
 * new seed, while quitting tears the Game down and returns to the title screen.
 * Two buttons, two sentences.
 *
 * **And a third verb the run itself must not blur** — issue #108. For the whole
 * project `game.ts` ran Abandon through `finishRun('death', null, null)`, so
 * this comment was true about the menu and false about the run: every voluntary
 * exit was banked as a death, inflating `stats.deaths` and `lifetime.deaths`
 * with exactly the runs that are *not* evidence of difficulty, and collecting
 * the Ash Courier's carry and the Cartographer's Echo — both of which
 * `bankRun` gates on a death — so walking away was a strictly better way to
 * farm the Echo than dying for it. `RunOutcomeKind` has had `'abandon'` since
 * v1 and `uiAdapter`'s `OUTCOME_TITLE` has had `Run Abandoned` since the death
 * screen landed; nothing selected either. `tests/gameOver.test.ts` now asserts
 * both directions, because a fix that merely stopped counting deaths would be
 * worse than the defect.
 */
export type PauseAction =
  | 'resume'
  | 'loadout'
  | 'codex'
  | 'map'
  | 'settings'
  | 'quitToTitle'
  | 'abandon';

export interface PauseItemVM {
  id: PauseAction;
  label: string;
}

/**
 * Which entries the menu should show, given what the shell has actually wired.
 *
 * The two shell entries are omitted rather than disabled when no action backs
 * them. A greyed-out row is a promise; an absent row is the truth. This project
 * has shipped dead buttons before and the cost was a day of looking for the
 * feature behind them.
 */
export function pauseMenuItems(has: {
  settings: boolean;
  quitToTitle: boolean;
}): PauseItemVM[] {
  const items: PauseItemVM[] = [
    { id: 'resume', label: 'Resume' },
    { id: 'loadout', label: 'Loadout' },
    { id: 'codex', label: 'Codex' },
    { id: 'map', label: 'Map' },
  ];
  if (has.settings) items.push({ id: 'settings', label: 'Settings' });
  if (has.quitToTitle) items.push({ id: 'quitToTitle', label: 'Quit to title' });
  // Last, and last on purpose: it is the only irreversible entry here.
  items.push({ id: 'abandon', label: 'Abandon run' });
  return items;
}

// ----------------------------------------------------------- the death screen

/**
 * The postmortem — docs/23.
 *
 * A screen, not a scene: the `Game` behind it keeps stepping, so this is a
 * modal in the same layer as the pause panel and **not** an `AppScene`. Two
 * consequences are visible right here in the types. There is no `dt`, no
 * revision counter and no per-frame anything, because the whole view model is
 * built **once**, at the instant of death, by `core/uiAdapter.ts`'s
 * `buildGameOverVM` — a run that has ended cannot change, and a sim that keeps
 * running behind a snapshot must not be allowed to edit it. And `log` is a
 * copy rather than a live reference, because `Game.events` is a 64-line ring
 * buffer that the still-running simulation keeps pushing material reactions
 * into; read live, the sentence that explains the death scrolls out from under
 * the player while they are reading it.
 */

export type GameOverAction = 'continue' | 'quitToTitle';

export interface GameOverItemVM {
  id: GameOverAction;
  label: string;
  /** The one-line honest description of what pressing this does. `title.ts`'s rule. */
  note: string;
}

/**
 * Which exits the screen offers.
 *
 * Same shape and same rule as `pauseMenuItems`: the shell entry is *omitted*
 * rather than greyed out when nothing is behind it, because a greyed-out row is
 * a promise and an absent row is the truth.
 *
 * `continue` is unconditional and is always first. There is no third option and
 * in particular there is no "dismiss": the run is over, and a death screen you
 * can close and walk away from would be a corpse with a working inventory.
 */
export function gameOverActions(has: { quitToTitle: boolean }): GameOverItemVM[] {
  const items: GameOverItemVM[] = [
    {
      id: 'continue',
      label: 'Continue',
      note: 'a new run, a new seed — this world is spent',
    },
  ];
  if (has.quitToTitle) {
    items.push({ id: 'quitToTitle', label: 'Quit to title', note: 'bank and put it down' });
  }
  return items;
}

/** How a figure should read. `bad` is a loss, not an error. */
export type GameOverTone = 'plain' | 'good' | 'bad';

export interface GameOverRowVM {
  key: string;
  label: string;
  /** Pre-formatted. The adapter owns every number's shape; this file draws it. */
  value: string;
  /** A quieter second line — the detail behind the number, when there is one. */
  note?: string;
  tone?: GameOverTone;
}

export interface GameOverSectionVM {
  id: string;
  title: string;
  rows: readonly GameOverRowVM[];
  /**
   * What to print when `rows` is empty.
   *
   * Required, not optional, and that is the point: every section on this screen
   * can legitimately be empty (a run that killed nothing, banked nothing, picked
   * nothing up), and a section that renders as a blank box reads as broken. An
   * empty section says *what* was empty — "you took nothing from this world" —
   * which is itself a fact about the run.
   */
  empty: string;
}

export interface GameOverVM {
  /** `You Died`, `Extracted`, `Run Abandoned`. */
  title: string;
  /**
   * `Killed by Lava` — `meta/runState`'s `causeHeadline`, verbatim, the same
   * string the toast said. Null when the run did not end in a death.
   */
  cause: string | null;
  /** `killed by lava (fire)` — the log's wording, which carries the damage type. */
  causeDetail: string | null;
  /** `The Cinder March · 4:12.87 · seed 8f2a1`. One line, always present. */
  epitaph: string;
  sections: readonly GameOverSectionVM[];
  /**
   * The tail of the run log, **oldest first**, snapshotted at death.
   *
   * This is the whole argument of docs/06 §4 rendered as a list: a five-step
   * chain reaction is magic until it is a sentence, and this is where the
   * sentences are. Ordered oldest-first so it reads as a chain ending in the
   * cause, rather than as a stack the player has to read upwards.
   */
  log: readonly string[];
  actions: readonly GameOverItemVM[];
}

// ------------------------------------------------------------------ pure helpers
//
// Derivations over view models only — no DOM, no game state. Kept here so the
// panels stay presentation code and so this logic is trivially testable.

/**
 * The id the inventory keys a weapon by.
 *
 * A real holding is keyed by its instance; a `devCatalogue` row has no instance
 * and falls back to its definition, which is right because a catalogue row is
 * one-per-definition by construction. Every comparison against `slots`,
 * `hands`, a loadout or a quick pair goes through this — `WeaponVM.id` on its
 * own is a definition and answers a different question.
 */
export function keyOf(w: WeaponVM): string {
  return w.instanceId ?? w.id;
}

export function formatStatValue(v: number, f?: StatFormat, unit?: string): string {
  let s: string;
  switch (f) {
    case 'dec1':
      s = v.toFixed(1);
      break;
    case 'dec2':
      s = v.toFixed(2);
      break;
    case 'pct':
      s = Math.round(v * 100) + '%';
      break;
    case 'ms':
      s = Math.round(v) + 'ms';
      break;
    case 'x':
      s = '×' + v.toFixed(2);
      break;
    default:
      s = Math.round(v).toString();
  }
  return unit ? s + unit : s;
}

export function signedStat(v: number, f?: StatFormat, unit?: string): string {
  const body = formatStatValue(Math.abs(v), f, unit);
  return (v > 0 ? '+' : v < 0 ? '−' : '±') + body;
}

export interface CompareCellVM {
  present: boolean;
  text: string;
  delta: number;
  deltaText: string;
  /** 1 better than baseline, -1 worse, 0 same. Colour-coded by the panel. */
  dir: -1 | 0 | 1;
}

export interface CompareRowVM {
  key: string;
  label: string;
  cells: CompareCellVM[];
}

const EMPTY_CELL: CompareCellVM = { present: false, text: '—', delta: 0, deltaText: '', dir: 0 };

/**
 * Side-by-side comparison of up to 3 weapons (docs/04 §7). Column 0 is the
 * baseline; every other column is scored against it. The union of stat keys is
 * taken in first-appearance order so a weapon with an extra stat still shows it
 * rather than silently dropping it.
 */
export function buildComparison(weapons: WeaponVM[]): CompareRowVM[] {
  const rows: CompareRowVM[] = [];
  const seen = new Map<string, CompareRowVM>();

  for (const w of weapons) {
    for (const s of w.stats) {
      if (seen.has(s.key)) continue;
      const row: CompareRowVM = { key: s.key, label: s.label, cells: [] };
      seen.set(s.key, row);
      rows.push(row);
    }
  }

  for (const row of rows) {
    const base = weapons[0]?.stats.find((s) => s.key === row.key);
    for (const w of weapons) {
      const s = w.stats.find((x) => x.key === row.key);
      if (!s) {
        row.cells.push(EMPTY_CELL);
        continue;
      }
      const text = formatStatValue(s.value, s.format, s.unit);
      if (!base || w === weapons[0]) {
        row.cells.push({ present: true, text, delta: 0, deltaText: '', dir: 0 });
        continue;
      }
      const delta = s.value - base.value;
      const better = s.higherIsBetter === false ? -1 : 1;
      const dir: -1 | 0 | 1 = delta === 0 ? 0 : delta * better > 0 ? 1 : -1;
      row.cells.push({
        present: true,
        text,
        delta,
        deltaText: delta === 0 ? '' : signedStat(delta, s.format, s.unit),
        dir,
      });
    }
  }
  return rows;
}

/**
 * A module's stat line, as the player reads it.
 *
 * `StatModVM.delta` is the *multiplier itself* in `mul` mode (1.25, not 0.25) —
 * the panel used to render `1 + delta`, so a ×1.25 damage core printed "×2.25",
 * and the good/bad colour tested `delta > 0`, which is true of every multiplier
 * ever authored. Both derivations live here now so the two call sites cannot
 * drift apart again.
 */
export function moduleStatText(s: StatModVM): string {
  if (s.mode === 'mul') return '×' + s.delta.toFixed(2);
  return signedStat(s.delta, s.format);
}

/** True when this modifier moves the stat in the direction the player wants. */
export function moduleStatIsGood(s: StatModVM): boolean {
  const up = s.mode === 'mul' ? s.delta > 1 : s.delta > 0;
  return up === (s.higherIsBetter !== false);
}

/**
 * Side-by-side comparison of up to 3 modules.
 *
 * Module choice is the buildcraft decision — a weapon is picked once a run,
 * modules are re-slotted constantly and for free (docs/04 §7) — so it needs the
 * same surface weapons have. The union of keys is taken in first-appearance
 * order, and a module that simply does not touch a stat reads as an absence
 * rather than as a zero, because "does nothing here" and "adds nothing here"
 * are different arguments for installing it.
 */
export function buildModuleComparison(modules: ModuleVM[]): CompareRowVM[] {
  const rows: CompareRowVM[] = [];
  const seen = new Map<string, CompareRowVM>();

  for (const m of modules) {
    for (const s of m.statMods) {
      if (seen.has(s.key)) continue;
      const row: CompareRowVM = { key: s.key, label: s.label, cells: [] };
      seen.set(s.key, row);
      rows.push(row);
    }
  }

  for (const row of rows) {
    const base = modules[0]?.statMods.find((s) => s.key === row.key);
    for (const m of modules) {
      const s = m.statMods.find((x) => x.key === row.key);
      if (!s) {
        row.cells.push(EMPTY_CELL);
        continue;
      }
      const text = moduleStatText(s);
      if (!base || m === modules[0]) {
        row.cells.push({ present: true, text, delta: 0, deltaText: '', dir: 0 });
        continue;
      }
      // Multipliers and additions are never mixed within a key — `statMods`
      // emits one mode per stat — so a plain subtraction is meaningful in both.
      const delta = s.delta - base.delta;
      const better = s.higherIsBetter === false ? -1 : 1;
      const dir: -1 | 0 | 1 = delta === 0 ? 0 : delta * better > 0 ? 1 : -1;
      row.cells.push({
        present: true,
        text,
        delta,
        deltaText: delta === 0 ? '' : signedStat(delta, s.mode === 'mul' ? 'dec2' : s.format),
        dir,
      });
    }
  }
  return rows;
}

/**
 * The rhythm meter's payoff, in the player's words (docs/04 §8).
 *
 * The fractions are passed in rather than baked here: they belong to combat,
 * and the previous hard-coded 25%/18% did not match the 35%/30% the code
 * actually applied — a HUD that lies about the size of a bonus is worse than
 * one that omits it.
 */
export function rhythmBonusText(
  rhythm: number,
  damageFrac: number,
  recoveryFrac: number,
): string {
  if (rhythm <= 0.02) return '';
  const dmg = Math.round(rhythm * damageFrac * 100);
  const rec = Math.round(rhythm * recoveryFrac * 100);
  return `+${dmg}% dmg · −${rec}% recovery`;
}

function intersects(a: readonly string[], b: readonly string[]): string[] {
  const out: string[] = [];
  for (const x of a) if (b.includes(x)) out.push(x);
  return out;
}

/**
 * docs/04 §7.1 verbatim: what detonates what this primes, what primes what this
 * detonates, and the terrain line. Every entry states its reason in words —
 * that is the whole discoverability strategy, so a line without a reason is a
 * bug, not a shortcut.
 */
export function computeSynergy(w: WeaponVM, inv: InventoryVM): SynergyHintVM {
  const hint: SynergyHintVM = {
    weaponId: w.id,
    title: w.name.toUpperCase(),
    primes: w.primes,
    detonates: w.detonates,
    modulesDetonatingMyPrimers: [],
    weaponsPrimingMyDetonations: [],
    terrainText: w.terrainText,
    warnings: [],
  };

  const self = keyOf(w);
  for (const m of inv.modules) {
    const shared = intersects(m.detonates, w.primes);
    if (shared.length === 0) continue;
    const where =
      m.installedOn === self
        ? '(installed here)'
        : m.installedOn
          ? '(on ' +
            (inv.weapons.find((x) => keyOf(x) === m.installedOn)?.name ?? 'another weapon') +
            ')'
          : '(loose)';
    hint.modulesDetonatingMyPrimers.push({
      subject: m.name,
      where,
      reason: shared.join('/') + ' detonation → ' + (m.detonationPayoff ?? m.effectText),
      refId: m.id,
    });
  }

  for (const other of inv.weapons) {
    const key = keyOf(other);
    if (key === self) continue;
    const shared = intersects(other.primes, w.detonates);
    if (shared.length === 0) continue;
    // Where the weapon *is* is half the advice: "in your other hand" and "back
    // at the stash" are the same synergy and completely different instructions.
    // This answered "(stash)" for anything outside the eight carried slots
    // while no stash existed at all; the stash is now its own list of
    // definitions (`inv.stash`) and never appears in `inv.weapons`, so the
    // honest answers here are the three a *carried* weapon can have.
    const where =
      inv.hands.main === key
        ? '(main hand)'
        : inv.hands.off === key
          ? '(off-hand)'
          : inv.slots.includes(key)
            ? '(carried)'
            : '(not held)';
    const lays = other.primeVerb ?? 'applies ' + shared.join('/');
    hint.weaponsPrimingMyDetonations.push({
      subject: other.name,
      where,
      reason: lays + ' → this ' + (w.detonateVerb ?? 'detonates') + ' it',
      refId: key,
    });
  }

  if (w.selfRiskText) hint.warnings.push(w.selfRiskText);
  for (const s of w.slots) {
    if (!s.moduleId) continue;
    const m = inv.modules.find((x) => x.id === s.moduleId);
    if (m?.selfRiskText) hint.warnings.push(m.name + ': ' + m.selfRiskText);
  }

  return hint;
}

/** Free-text search corpus: name, class, family, tags, effect text (docs/04 §7). */
export function weaponSearchBlob(w: WeaponVM): string {
  return (
    w.name +
    ' ' +
    w.className +
    ' ' +
    w.archetype +
    ' ' +
    w.damageType +
    ' ' +
    (w.family ?? '') +
    ' ' +
    w.rarity +
    ' ' +
    w.tags.join(' ') +
    ' ' +
    w.primes.join(' ') +
    ' ' +
    w.detonates.join(' ') +
    ' ' +
    w.effectText +
    ' ' +
    w.terrainText +
    ' ' +
    // The verbs are the words the synergy prose actually shows the player
    // ("shatters", "sprays ICE"), so searching for what you read must work.
    (w.detonateVerb ?? '') +
    ' ' +
    (w.primeVerb ?? '')
  ).toLowerCase();
}

export function moduleSearchBlob(m: ModuleVM): string {
  return (
    m.name +
    ' ' +
    m.family +
    ' ' +
    m.slot +
    ' ' +
    m.rarity +
    ' ' +
    (m.damageType ?? '') +
    ' ' +
    m.tags.join(' ') +
    ' ' +
    m.primes.join(' ') +
    ' ' +
    m.detonates.join(' ') +
    ' ' +
    m.effectText +
    ' ' +
    (m.selfRiskText ?? '')
  ).toLowerCase();
}

/** Does this module physically fit that weapon? Drives the compatibility filter. */
export function moduleFitsWeapon(m: ModuleVM, w: WeaponVM): boolean {
  if (m.compatibleClasses && !m.compatibleClasses.includes(w.className)) return false;
  return w.slots.some((s) => s.kind === m.slot);
}

/** First empty slot of the module's kind, else the last one of that kind. */
export function targetSlotFor(m: ModuleVM, w: WeaponVM): SlotVM | null {
  let last: SlotVM | null = null;
  for (const s of w.slots) {
    if (s.kind !== m.slot) continue;
    if (!s.moduleId) return s;
    last = s;
  }
  return last;
}

/** Sort comparator: favourites first, then rarity desc, then name. */
export function byFavouriteThenRarity(
  a: { favourite: boolean; rarity: Rarity; name: string },
  b: { favourite: boolean; rarity: Rarity; name: string },
): number {
  if (a.favourite !== b.favourite) return a.favourite ? -1 : 1;
  const ra = RARITY_ORDER.indexOf(a.rarity);
  const rb = RARITY_ORDER.indexOf(b.rarity);
  if (ra !== rb) return rb - ra;
  return a.name.localeCompare(b.name);
}
